/**
 * Resolve the project scope the sidebar should render.
 *
 * A scope key that names no known project falls back to the full list rather than
 * hiding everything. That covers both a project that was removed and a project list
 * that is merely unavailable while an Environment reconnects.
 */
export function resolveSidebarProjectScope<TProject extends { readonly projectKey: string }>(
  selectedScopeKey: string | null,
  projects: readonly TProject[],
): readonly TProject[] {
  const visibleScopeKey = resolveVisibleSidebarProjectScopeKey(selectedScopeKey, projects);
  if (visibleScopeKey === null) {
    return projects;
  }
  return projects.filter((project) => project.projectKey === visibleScopeKey);
}

/** Preserve the saved scope while its Environment is away, but present the visible scope. */
export function resolveVisibleSidebarProjectScopeKey(
  selectedScopeKey: string | null,
  projects: readonly { readonly projectKey: string }[],
): string | null {
  return selectedScopeKey !== null &&
    projects.some((project) => project.projectKey === selectedScopeKey)
    ? selectedScopeKey
    : null;
}

/**
 * Decide whether a thread belongs to the selected scope.
 *
 * Knowing no projects at all is not the same as a scope that excludes everything: until
 * an Environment delivers its projects the sidebar has nothing to scope against, and
 * filtering on that would blank a list the user is still looking at.
 */
export function scopeAllowsThread(input: {
  readonly hasKnownProjects: boolean;
  readonly scopedProjectRefKeys: ReadonlySet<string>;
  readonly threadProjectRefKey: string;
}): boolean {
  return !input.hasKnownProjects || input.scopedProjectRefKeys.has(input.threadProjectRefKey);
}
